import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { userApi, apiError } from "@/lib/userApi";
import { toast } from "sonner";
import { MessageCircle, Share2, MoreVertical, Trash2, Pencil, Flag, Pin, Megaphone, Check, MessageSquareOff, Users } from "lucide-react";
import { Avatar } from "@/components/community/ChatList";
import { FriendAddButton } from "@/components/social/FriendAddButton";
import { StarBadge } from "@/components/social/Stars";
import { Comments } from "@/components/feed/Comments";
import { MediaLightbox } from "@/components/feed/MediaLightbox";
import { RichText } from "@/components/feed/MentionInput";
import { stormPulse } from "@/lib/pulse";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const src = (m) => `${BACKEND}${m.url}`;

export const REACTIONS = [
  { key: "like", emoji: "👍", label: "Me gusta" },
  { key: "love", emoji: "❤️", label: "Me encanta" },
  { key: "haha", emoji: "😂", label: "Me divierte" },
  { key: "wow", emoji: "😮", label: "Me asombra" },
  { key: "sad", emoji: "😢", label: "Me entristece" },
  { key: "angry", emoji: "😡", label: "Me enoja" },
];

/** Publicación del muro: media, reacciones, comentarios y compartir. */
export const PostCard = ({ post, me, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [picker, setPicker] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.text);
  const [state, setState] = useState(post);
  const [zoom, setZoom] = useState(null);   // índice de la foto abierta a pantalla completa
  const reactionEmojis = useMemo(
    () => REACTIONS.filter((r) => state.counts?.[r.key]).map((r) => r.emoji).join(" "),
    [state.counts]);
  const mine = state.author?.key === me?.key;

  const react = async (reaction) => {
    stormPulse();
    try {
      const { data } = await userApi.post(`/feed/${state.post_id}/react`, { reaction });
      setState((s) => ({ ...s, ...data }));
    } catch (e) { toast.error(apiError(e)); }
    setPicker(false);
  };

  const share = async () => {
    try {
      await userApi.post("/feed", { share_of: state.share_of || state.post_id, text: "" });
      toast.success("Compartido en tu muro");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const remove = async () => {
    try {
      await userApi.delete(`/feed/${state.post_id}`);
      toast.success("Publicación eliminada");
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const saveEdit = async () => {
    try {
      await userApi.patch(`/feed/${state.post_id}`, { text: draft });
      setState((s) => ({ ...s, text: draft, edited_at: new Date().toISOString() }));
      setEditing(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setCommentsMode = async (mode) => {
    try {
      const { data } = await userApi.post(`/feed/${state.post_id}/comments-mode`, { mode });
      setState((s) => ({ ...s, comments_mode: data.comments_mode }));
      toast.success(mode === "nobody" ? "Comentarios desactivados"
        : mode === "friends" ? "Solo tus amigos pueden comentar"
        : "Comentarios abiertos a todos");
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const pin = async () => {
    try {
      const { data } = await userApi.post(`/feed/${state.post_id}/pin`);
      setState((s) => ({ ...s, pinned: data.pinned }));
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const report = async () => {
    const reason = window.prompt("¿Qué problema tiene esta publicación?");
    if (!reason || reason.trim().length < 3) return setMenu(false);
    try {
      await userApi.post("/community/report",
        { target: state.author?.key, reason: `Publicación: ${reason.trim()}` });
      toast.success("Reporte enviado al administrador");
    } catch (e) { toast.error(apiError(e)); }
    setMenu(false);
  };

  const mineReaction = REACTIONS.find((r) => r.key === state.my_reaction);
  const Media = ({ list }) => (
    <div className={`mt-3 grid gap-1.5 ${list.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
      {list.map((m, i) => m.mime?.startsWith("video/") ? (
        <video key={m.media_id} src={src(m)} controls
          data-testid={`post-video-${m.media_id}`}
          className="w-full rounded-xl border border-white/10 bg-black/60"
          style={{ maxHeight: list.length > 1 ? "46vh" : "78vh" }} />
      ) : (
        <button key={m.media_id} type="button" onClick={() => setZoom(i)}
          data-testid={`post-image-${m.media_id}`}
          className="block w-full rounded-xl border border-white/10 bg-black/60 overflow-hidden relative">
          <img src={src(m)} alt={m.name}
            className="w-full object-contain"
            style={{ maxHeight: list.length > 1 ? "46vh" : "78vh" }} />
          {list.length > 1 && (
            <span className="absolute bottom-2 right-2 text-[10px] px-1.5 py-0.5 rounded-full text-white/85"
              style={{ background: "rgba(10,10,14,0.75)" }}>
              {i + 1}/{list.length}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <article className="glass-card p-4" data-testid={`post-${state.post_id}`}>
      <div className="flex items-start gap-3">
        <Link to={`/perfil/${state.author?.key}`} data-testid={`post-avatar-${state.post_id}`}>
          <Avatar person={state.author} size={42} />
        </Link>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link to={`/perfil/${state.author?.key}`}
              className="text-sm font-semibold text-white truncate hover:text-red-300">
              {state.author?.name}
            </Link>
            <StarBadge level={state.author?.level} />
            {state.author?.ambassador && (
              <span title="Embajador del mes" data-testid={`post-ambassador-${state.post_id}`}
                className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300/50 text-amber-200 flex items-center gap-1">
                <img src="/logo-digital-forge.jpg" alt="" className="w-3 h-3 rounded-full" /> EMBAJADOR
              </span>
            )}
            {!mine && <FriendAddButton personKey={state.author?.key} name={state.author?.name} />}
            {state.kind === "anuncio" && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white flex items-center gap-1">
                <Megaphone className="w-2.5 h-2.5" /> ANUNCIO
              </span>
            )}
            {state.pinned && <Pin className="w-3 h-3 text-red-400" />}
          </div>
          <div className="text-[11px] text-white/40">
            {(state.created_at || "").slice(0, 16).replace("T", " ")}
            {state.edited_at && " · editado"}
          </div>
        </div>
        <div className="relative shrink-0">
          <button data-testid={`post-menu-${state.post_id}`} onClick={() => setMenu((v) => !v)}
            className="text-white/50 hover:text-white p-1"><MoreVertical className="w-4 h-4" /></button>
          {menu && (
            <div className="absolute right-0 top-8 glass-card p-1.5 w-48 z-30" data-testid={`post-menu-open-${state.post_id}`}>
              {mine && (
                <button onClick={() => { setEditing(true); setMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
              )}
              {mine && (
                <>
                  <button data-testid={`post-comments-nobody-${state.post_id}`}
                    onClick={() => setCommentsMode(state.comments_mode === "nobody" ? "all" : "nobody")}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                    <MessageSquareOff className="w-3.5 h-3.5" />
                    {state.comments_mode === "nobody" ? "Activar comentarios" : "Desactivar comentarios"}
                  </button>
                  <button data-testid={`post-comments-friends-${state.post_id}`}
                    onClick={() => setCommentsMode(state.comments_mode === "friends" ? "all" : "friends")}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                    <Users className="w-3.5 h-3.5" />
                    {state.comments_mode === "friends" ? "Abrir a todos" : "Solo amigos comentan"}
                  </button>
                </>
              )}
              {me?.is_admin && (
                <button data-testid={`post-pin-${state.post_id}`} onClick={pin}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/85 hover:bg-white/8">
                  <Pin className="w-3.5 h-3.5" /> {state.pinned ? "Quitar de fijados" : "Fijar arriba"}
                </button>
              )}
              {(mine || me?.is_admin) && (
                <button data-testid={`post-delete-${state.post_id}`} onClick={remove}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-rose-300 hover:bg-white/8">
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              )}
              {!mine && (
                <button data-testid={`post-report-${state.post_id}`} onClick={report}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-rose-300 hover:bg-white/8">
                  <Flag className="w-3.5 h-3.5" /> Reportar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {editing ? (
        <div className="mt-3 flex items-end gap-2">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2}
            data-testid={`post-edit-input-${state.post_id}`}
            className="glass-field flex-1 px-3 py-2 rounded-xl text-sm text-white outline-none resize-none" />
          <button data-testid={`post-edit-save-${state.post_id}`} onClick={saveEdit}
            className="glass-btn glass-btn-red w-9 h-9 rounded-xl flex items-center justify-center">
            <Check className="w-4 h-4" />
          </button>
        </div>
      ) : state.text ? (
        <p className="mt-3 text-[15px] text-white whitespace-pre-wrap break-words">
          {state.deleted ? <span className="italic text-white/40">Publicación eliminada</span> : <RichText text={state.text} />}
        </p>
      ) : null}

      {state.media?.length > 0 && <Media list={state.media} />}

      {state.shared_post && (
        <div className="mt-3 glass-card p-3" data-testid={`shared-${state.shared_post.post_id}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <Avatar person={state.shared_post.author} size={26} />
            <span className="text-xs text-white/80">{state.shared_post.author?.name}</span>
          </div>
          <p className="text-sm text-white/85 whitespace-pre-wrap"><RichText text={state.shared_post.text} /></p>
          {state.shared_post.media?.length > 0 && <Media list={state.shared_post.media} />}
        </div>
      )}

      {(state.total_reactions > 0 || state.comments_count > 0) && (
        <div className="flex items-center gap-3 mt-3 text-[11px] text-white/50">
          {state.total_reactions > 0 && (
            <span data-testid={`post-reactions-${state.post_id}`}>
              {reactionEmojis} {state.total_reactions}
            </span>
          )}
          {state.comments_count > 0 && <span>{state.comments_count} comentario(s)</span>}
          {state.shares > 0 && <span>{state.shares} compartido(s)</span>}
        </div>
      )}

      <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-white/8 relative">
        <button data-testid={`post-react-${state.post_id}`}
          onClick={() => react(mineReaction ? mineReaction.key : "like")}
          onMouseEnter={() => setPicker(true)}
          className={`glass-btn ${mineReaction ? "glass-btn-red" : ""} flex-1 px-3 py-2 rounded-xl text-xs flex items-center justify-center gap-1.5`}>
          <span>{mineReaction?.emoji || "👍"}</span> {mineReaction?.label || "Reaccionar"}
        </button>
        <button data-testid={`post-comment-${state.post_id}`} onClick={() => setOpen((v) => !v)}
          disabled={state.comments_mode === "nobody" && !mine}
          className="glass-btn flex-1 px-3 py-2 rounded-xl text-xs flex items-center justify-center gap-1.5 disabled:opacity-40">
          {state.comments_mode === "nobody" ? <MessageSquareOff className="w-3.5 h-3.5" />
            : <MessageCircle className="w-3.5 h-3.5" />}
          {state.comments_mode === "nobody" ? "Sin comentarios"
            : state.comments_mode === "friends" ? "Comentar (amigos)" : "Comentar"}
        </button>
        <button data-testid={`post-share-${state.post_id}`} onClick={share}
          className="glass-btn flex-1 px-3 py-2 rounded-xl text-xs flex items-center justify-center gap-1.5">
          <Share2 className="w-3.5 h-3.5" /> Compartir
        </button>

        {picker && (
          <div onMouseLeave={() => setPicker(false)}
            className="absolute -top-12 left-0 glass-card px-2 py-1.5 flex gap-1 z-20"
            data-testid={`reaction-picker-${state.post_id}`}>
            <button data-testid={`reaction-picker-close-${state.post_id}`} onClick={() => setPicker(false)}
              className="text-xs text-white/40 px-1">×</button>
            {REACTIONS.map((r) => (
              <button key={r.key} title={r.label} data-testid={`react-${r.key}-${state.post_id}`}
                onClick={() => react(r.key)}
                className="text-xl hover:scale-125 transition-transform px-0.5">{r.emoji}</button>
            ))}
          </div>
        )}
      </div>

      {open && (
        <Comments postId={state.post_id} me={me}
          onCountChange={() => setState((s) => ({ ...s, comments_count: (s.comments_count || 0) + 1 }))} />
      )}

      {zoom !== null && state.media?.length > 0 && (
        <MediaLightbox list={state.media} index={zoom} onIndex={setZoom}
          onClose={() => setZoom(null)} post={state} me={me} reactions={REACTIONS}
          onReact={react}
          onCommentAdded={() => setState((s) => ({ ...s, comments_count: (s.comments_count || 0) + 1 }))} />
      )}
    </article>
  );
};

export default PostCard;
